import axios from 'axios'
import posthog from 'posthog-js'
import { ApiConfig } from 'config'
import { usageRequestHeaders } from 'utils/usage-request-context'

const client = axios.create({
	baseURL: ApiConfig.API_BASE_URL
})

let TOKEN: string | null = null
let IMPERSONATION: string | null = null

client.interceptors.request.use((config) => {
	const token = TOKEN
	const impersonation = IMPERSONATION

	if (token && config.headers) {
		config.headers.Authorization = `Bearer ${token}`
	}

	if (impersonation && config.headers) {
		config.headers['Impersonated-User-ID'] = impersonation
	}
	if (config.headers && typeof window !== 'undefined') {
		Object.assign(config.headers, usageRequestHeaders(window))
	}

	// Link backend logs/traces to the PostHog session (and replay). Guarded: PostHog
	// may not be initialised yet on the earliest requests.
	try {
		const sessionId = posthog?.get_session_id?.()
		if (sessionId && config.headers) {
			config.headers['x-posthog-session-id'] = sessionId
		}
	} catch {
		// PostHog not ready; skip
	}
	return config
})

// Surface the backend trace reference (errorCode) to users on server errors, so they can
// share it with support to pinpoint the exact request. Only for 5xx — client/validation
// errors (4xx) stay clean. Mutating data.message here covers every call site (parseError
// and direct error.response.data.message access) without touching each one.
client.interceptors.response.use(undefined, async (error) => {
	const status = error?.response?.status
	const data = error?.response?.data
	if (status >= 500 && data?.errorCode && typeof data.message === 'string' && !data.message.includes(data.errorCode)) {
		data.message = `${data.message} (Ref: ${data.errorCode})`
	}
	return Promise.reject(error)
})

const setLogoutListener = (logout: () => void) => {
	client.interceptors.response.use(undefined, async (error) => {
		if (error?.response?.status === 401) {
			logout()
		}
		return Promise.reject(error)
	})
}
const setUnverifiedEmailError = (navigateToError: () => void) => {
	client.interceptors.response.use(undefined, async (error) => {
		if (error?.response?.status === 423) {
			if (window?.location?.pathname !== '/emails/verify') {
				navigateToError()
			}
		}
		return Promise.reject(error)
	})
}

const setAuthToken = (token: string) => {
	TOKEN = token
}

const getAuthToken = () => TOKEN

const setImpersonation = (value: string | null) => {
	IMPERSONATION = value
}

export const ApiClient = {
	client,
	setAuthToken,
	setImpersonation,
	setLogoutListener,
	setUnverifiedEmailError,
	getAuthToken
}
